import React, { FC, useEffect, useState } from "react";
import { differenceInMinutes, format, parseISO, set } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  EAcaoLogUser,
  EApplication,
  ECursoGrau,
  EGrupoAcaoLogUser,
  ELogUserStatus,
  EUserType,
  IAulasChamada,
  IDiaAula,
  Log,
} from "@deltasge/marauders-map";
import { Alert, Grid, GridSize } from "@mui/material";
import { Filter as FilterIcon } from "mdi-material-ui";

import { Float } from "components/Buttons/Float";
import { EmptyState } from "components/EmptyStates";
import { snack } from "components/GlobalSnackbar";
import { api, history } from "configs";
import { useApi } from "hooks/useApi";
import { useDialog } from "hooks/useDialog";
import { useFilter } from "hooks/useFilter";
import { useMobile } from "hooks/useMobile";
import { IFilterAula } from "interfaces";
import { Main } from "pages/_layouts/Main";
import { useAppDispatch, useAppSelector } from "store";
import { request as getCategorias } from "store/modules/categorias";
import { request as getFeriados } from "store/modules/feriados";
import { request as getModelos } from "store/modules/relatorios/diarios/modelos";
import { request as getPapeis } from "store/modules/relatorios/configuracoes/papeis";
import { getError } from "utils";
import { gravaLogUser } from "configs/log";
import { Backdrop } from "components/Backdrop";
import { DiaAulaCadastro } from "./components/DiaAulaCadastro";
import { Diario } from "./components/Diario";
import { Filter } from "./components/Filter";
import { Item } from "./components/Item";
import { impressaoRapidaDiario } from "./util";

import image1 from "assets/images/turma1.jpg";
import image2 from "assets/images/turma2.jpg";
import image3 from "assets/images/turma3.jpg";
import image4 from "assets/images/turma4.jpg";
import image5 from "assets/images/turma5.jpg";
import image6 from "assets/images/turma6.jpg";
import image7 from "assets/images/turma7.jpg";
import image8 from "assets/images/turma8.jpg";
import image9 from "assets/images/turma9.jpg";
import image10 from "assets/images/turma10.jpg";
import { CadastroTurmaObs } from "pages/TurmaObs/Cadastro/CadastroTurmaObs";

const images = [
  image1,
  image2,
  image3,
  image4,
  image5,
  image6,
  image7,
  image8,
  image9,
  image10,
];

export const Home: FC = () => {
  const {
    escola: { anoSite },
    usuario: { usuario },
    configDiarioPadrao,
    configPapelPadrao,
    mensagemCategoria: { data: mensagemCategorias },
    modelosDiarios: { data: modelosDiarios },
    tamanhoPapeis: { data: tamanhoPapeis },
    feriados: { data: feriados },
    layout: {
      rightbar: { isOpen: rightbarIsOpen },
    },
    configuracoes: {
      etapas,
      configOnline: { configuracoes },
    },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [aula, setAula] = useState<IAulasChamada>();
  const [gerandoAula, setGerandoAula] = useState(false);

  const handleFilterCurso = (idCurso?: number) =>
    navigate({
      ...filter,
      idCurso,
      idSerie: undefined,
      idTurma: undefined,
    });

  const handleFilterSerie = (idSerie?: number) =>
    navigate({
      ...filter,
      idSerie,
      idTurma: undefined,
    });

  const handleFilterTurma = (idTurma?: number) =>
    navigate({
      ...filter,
      idTurma,
    });

  const handleFilterData = (data?: Date) => {
    if (data) {
      navigate({
        ...filter,
        data: set(data, { hours: 12, minutes: 0, seconds: 0 }),
      });
    }
  };

  const {
    Component,
    navigate,
    filter,
    selecionados: { curso, serie, turma },
  } = useFilter<IFilterAula>({
    pathname: "/",
    queryKeys: [
      "data",
      "idCurso",
      "idSerie",
      "idTurma",
      "idMateria",
      "idProfessor",
      "filtrarSemDescricao",
      "todosFazerFeitos",
    ],
    initialValue: {
      anoLetivo: parseInt(anoSite, 10),
      data: set(new Date(), { hours: 12, minutes: 0, seconds: 0 }),
      idProfessor:
        usuario?.tipoUsuario == EUserType.Professor
          ? usuario?.id ?? undefined
          : undefined,
      todosFazerFeitos: 0,
      filtrarSemDescricao: 0,
    },
    options: {
      curso: {
        onClick: handleFilterCurso,
        mostrarTodos: true,
      },
      serie: {
        onClick: handleFilterSerie,
        mostrarTodos: true,
      },
      turma: {
        onClick: handleFilterTurma,
        mostrarTodos: true,
      },
      data: {
        onClick: handleFilterData,
        allowsToClean: false,
        holidays: configuracoes.bloquear_digitacao_feriados
          ? feriados
          : undefined,
      },
    },
  });

  const {
    RenderDialog: DialogCadastroAula,
    show: showCadastro,
    hide: hideCadastro,
  } = useDialog({
    title: `Cadastrar aula para o ${turma?.tituloTurma} no dia ${format(
      filter.data,
      "dd/MM/yyyy"
    )} - ${format(filter.data, "EEEE", {
      locale: ptBR,
    }).rUpper()}`,
    options: {
      modal: true,
    },
  });

  const {
    RenderDialog: DialogDiario,
    show: showDiario,
    hide: hideDiario,
  } = useDialog({
    title: `Diário de classe - ${
      aula ? aula.turmaDescricao.concat(" - ").concat(aula.materiaNome) : ""
    }`,
    options: {
      modal: true,
    },
  });

  const {
    RenderDialog: DialogTurmaObs,
    show: showTurmaObs,
    hide: hideTurmaObs,
  } = useDialog({
    title: `Observação do dia ${format(filter.data, "dd/MM/yyyy")} - Turma ${
      aula?.turmaDescricao
    } - ${aula?.materiaNome?.rUpper()}`,
    options: {
      modal: true,
    },
  });

  let idProfessor: number | null = null;
  if (usuario?.tipoUsuario == EUserType.Professor && usuario.id) {
    idProfessor = usuario.id;
  } else if (filter.idProfessor) {
    idProfessor = filter.idProfessor;
  }

  let url: string | null = null;
  if (
    ((filter.idCurso && filter.idSerie && filter.idTurma) || idProfessor) &&
    filter.data
  ) {
    const params: Record<string, string> = {};
    if (filter.idCurso) params.idCurso = filter.idCurso.toString();
    if (filter.idSerie) params.idSerie = filter.idSerie.toString();
    if (filter.idTurma) params.idTurma = filter.idTurma.toString();
    const strData = format(filter.data, "yyyy-MM-dd HH:mm:ss");
    const query = new URLSearchParams(params).toString();
    url = `/educador/flexchamada/${strData}/professor/${idProfessor}?${query}`;
  }

  const {
    data: aulas,
    loading,
    carregar: carregarAulas,
  } = useApi<IAulasChamada[]>({
    url,
    onError: () => snack.error("Problema ao carregar as aulas"),
    refresh: filter.idProfessor?.toString(),
  });

  let aulasProfessor = idProfessor
    ? aulas?.filter(
        (a) =>
          a.idProfessorMateria === idProfessor ||
          a.idProfessorChamada === idProfessor
      )
    : aulas;
  if (aulasProfessor && (filter.todosFazerFeitos ?? 0) > 0) {
    if (filter.todosFazerFeitos === 1) {
      aulasProfessor = aulasProfessor.filter((a) => a.idDiaAulas === null);
    }
    if (filter.todosFazerFeitos === 2) {
      aulasProfessor = aulasProfessor.filter((a) => a.idDiaAulas !== null);
    }
  }

  if (aulasProfessor && (filter.filtrarSemDescricao ?? 0) > 0) {
    if (filter.filtrarSemDescricao === 1) {
      aulasProfessor = aulasProfessor.filter(
        (a) =>
          a.idDiaAulas === null ||
          a.descricaoChamada === null ||
          a.descricaoChamada === undefined ||
          a.descricaoChamada.length <= 3
      );
    }
    if (filter.filtrarSemDescricao === 2) {
      aulasProfessor = aulasProfessor.filter(
        (a) =>
          a.idDiaAulas && a.descricaoChamada && a.descricaoChamada.length > 3
      );
    }
  }

  if (aulasProfessor && curso) {
    aulasProfessor = aulasProfessor.filter((a) => a.idCurso === curso.id);
  }

  if (aulasProfessor && turma) {
    aulasProfessor = aulasProfessor.filter((a) => a.idTurma === turma.id);
  }

  const showImpressaoRapida =
    !!configDiarioPadrao?.modelo && !!configPapelPadrao?.width;

  useEffect(() => {
    (async () => {
      if (modelosDiarios.length == 0 || tamanhoPapeis.length == 0) {
        dispatch(getModelos());
        dispatch(getPapeis());
      }
      if (mensagemCategorias.length == 0) {
        dispatch(getCategorias());
      }
      if (!feriados || feriados.length == 0) {
        dispatch(getFeriados(parseInt(anoSite, 10)));
      }
    })();
  }, []);

  const handleChamadaDia = async (aula: IAulasChamada) => {
    history.push(
      `/chamada-dia/${format(filter.data, "yyyy-MM-dd HH:mm:ss")}/turma/${
        aula.idTurma
      }`
    );
  };

  const handleAula = async (aula: IAulasChamada) => {
    try {
      let idDiaAulas = aula.idDiaAulas;
      if (!aula.idDiaAulas) {
        setGerandoAula(true);
        const { data } = await api.post<IDiaAula | undefined | null>(
          "diaaula",
          {
            anoLetivo: anoSite,
            data: format(filter.data, "yyyy-MM-dd HH:mm:ss"),
            aula: aula.aula,
            idCurso: aula.idCurso,
            idSerie: aula.idSerie,
            idTurma: aula.idTurma,
            idMateria: aula.idMateria,
            diaSemAula: false,
            inutilizaDia: false,
            idProfessor:
              usuario?.tipoUsuario === EUserType.Professor ? usuario?.id : null,
            idUsuario:
              usuario?.tipoUsuario === EUserType.UsuarioSistema
                ? usuario?.id
                : null,
            descricao: "",
            idMensagem: null,
            tarefa: "",
            id: null,
            naGrade: true,
            hora: aula.horaIni,
            duracao: aula.duracao,
            origem: EApplication.Educador,
          }
        );

        if (!data) {
          throw new Error("Erro ao gerar o dia de aula");
        }
        await gravaLogUser({
          logKey: {
            ...Log.Alunos.Incluir,
            acao: EAcaoLogUser.Inclusao,
            grupoAcao: EGrupoAcaoLogUser.Aula,
          },
          logKeys: {
            idProfessor: Number(data.idProfessor),
            idMateria: Number(data.idMateria),
            idCurso: Number(data.idCurso),
            idSerie: Number(data.idSerie),
            idTurma: Number(data.idTurma),
          },
          status: ELogUserStatus.Aguardando,
          message: `incluiu aula id: ${data.id}`,
          dataAfter: { ...data },
        });
        idDiaAulas = data.id;
        setGerandoAula(false);
      }
      history.push(`/gerenciar-aula/${idDiaAulas}`);
    } catch (error) {
      snack.error(getError(error));
    }
  };

  const handleDiario = async (
    aula: IAulasChamada,
    showImpressaoRapida?: boolean
  ): Promise<void> => {
    if (!showImpressaoRapida) {
      setAula(aula);
      showDiario();
      return;
    }

    try {
      await impressaoRapidaDiario({
        idCurso: aula.idCurso,
        idSerie: aula.idSerie,
        idTurma: aula.idTurma,
        idMateria: aula.idMateria,
      });
    } catch (error) {
      snack.error(getError(error));
    }
  };

  const handleShowCadastro = () => {
    const grau = curso?.grau;
    const serieDbf =
      serie && serie.serie ? parseInt(serie.serie, 10) : undefined;
    if (!grau || !serieDbf) {
      snack.error("Erro ao encontrar o grau e a serie da turma selecionada");
      return;
    }
    if (
      configuracoes.blockfreqcontinf &&
      (grau == ECursoGrau.Infantil ||
        (grau == ECursoGrau.Fundamental && serieDbf <= 5))
    ) {
      snack.warning(
        "Inserir aulas fora da grade está desabilitado, contate a secretaria."
      );
      return;
    }
    if (
      configuracoes.blockfreqcont &&
      (grau == ECursoGrau.Medio ||
        (grau == ECursoGrau.Fundamental && serieDbf > 5 && serieDbf < 9))
    ) {
      snack.warning(
        "Inserir aulas fora da grade está desabilitado, contate a secretaria."
      );
      return;
    }
    showCadastro();
  };

  const handleTurmaObs = (aula: IAulasChamada) => {
    setAula(aula);
    showTurmaObs();
  };

  const isMd = useMobile("md");
  const isLg = useMobile("lg");
  const isXl = useMobile("xl");

  const mdSize: GridSize = !isMd && rightbarIsOpen ? 12 : 5;
  const lgSize: GridSize = isLg && rightbarIsOpen ? 4 : 3;
  const xlSize: GridSize = isXl && rightbarIsOpen ? 4 : 3;

  let etapa = configuracoes.bim_site;
  if (filter.idCurso && filter.data) {
    const etapaCurso = etapas.find(
      (f) =>
        f.idsCursos?.includes(filter.idCurso ?? 0) &&
        parseISO(f.dataEtapaIni ?? "").getTime() <= filter.data.getTime() &&
        parseISO(f.dataEtapaFim ?? "").getTime() >= filter.data.getTime()
    );
    etapa = etapaCurso?.etapa ?? configuracoes.bim_site;
  }

  const isFeriado =
    (configuracoes.bloquear_digitacao_feriados &&
      feriados?.includes(format(filter.data, "yyyy-MM-dd"))) ||
    false;

  return (
    <Main
      maxWidth={false}
      loading={loading}
      title="Aulas"
      rightbarChildren={
        <Filter
          filter={filter}
          navigate={navigate}
          Component={Component}
          curso={curso}
          serie={serie}
          turma={turma}
        />
      }
      rightbarIcon={<FilterIcon />}
    >
      <DialogTurmaObs>
        {aula && filter.anoLetivo && (
          <CadastroTurmaObs
            handleClose={hideTurmaObs}
            item={{
              anoLetivo: filter.anoLetivo.toString(),
              data: format(filter.data, "yyyy-MM-dd"),
              idCurso: aula.idCurso,
              idSerie: aula.idSerie,
              idTurma: aula.idTurma,
              idMateria: aula.idMateria,
            }}
          />
        )}
      </DialogTurmaObs>
      <DialogCadastroAula>
        <DiaAulaCadastro
          filtro={filter}
          close={hideCadastro}
          carregarAulas={carregarAulas}
        />
      </DialogCadastroAula>
      <DialogDiario>
        {aula && (
          <Diario
            turmaMateria={{
              idCurso: aula.idCurso,
              idSerie: aula.idSerie,
              idTurma: aula.idTurma,
              idMateria: aula.idMateria,
              idSistemaAvaliacao: serie?.idSistemaAvaliacao,
            }}
            close={hideDiario}
          />
        )}
      </DialogDiario>
      <Backdrop open={gerandoAula} message="Gerando dia de aula" />
      {!filter.idTurma && !idProfessor && !loading && (
        <EmptyState
          type="search"
          title="Selecione no filtro"
          subtitle="Selecione ao menos a data e a turma para apresentar as aulas do dia"
        />
      )}
      {aulasProfessor?.length == 0 && !loading && (
        <EmptyState
          type="404"
          title={`Não há aula na grade para o dia ${format(
            filter.data,
            "dd/MM/yyyy"
          )}`
            .concat(filter.idTurma ? " para esta turma" : "")
            .concat(
              usuario?.tipoUsuario != EUserType.Professor ? " ou professor" : ""
            )}
          subtitle={"Selecione no filtro ao lado outra data".concat(
            filter.idTurma
              ? " ou turma. Ou clique no botão abaixo para adicionar uma aula"
              : ". Ou selecione uma turma e clique em adicionar aula"
          )}
        />
      )}
      {aulasProfessor && aulasProfessor.length > 0 && (
        <Grid spacing={2} container alignItems="stretch">
          {isFeriado && (
            <Grid item xs={12}>
              <Alert severity="warning">
                Não é possível criar aulas para o dia
                {format(filter.data, "dd 'de' MMMM", { locale: ptBR })}, pois
                esta marcado como feriado
              </Alert>
            </Grid>
          )}
          {aulasProfessor.map((aula, i) => {
            let percentage: number | undefined = 0;
            let isFim = false;
            if (aula.horaFim.indexOf(":") > 0) {
              const horaFim = aula.horaFim.split(":");
              const data = set(filter.data, {
                hours: parseInt(horaFim[0], 10),
                minutes: parseInt(horaFim[1], 10),
              });
              isFim = new Date() > data;
            }
            if (isFim) percentage = 100;
            if (aula.horaIni.indexOf(":") > 0 && !isFim) {
              const horaIni = aula.horaIni.split(":");
              const data = set(filter.data, {
                hours: parseInt(horaIni[0], 10),
                minutes: parseInt(horaIni[1], 10),
              });
              const difference = differenceInMinutes(new Date(), data);
              if (difference > 0) {
                percentage = (difference * 100) / aula.duracao;
              }
            }
            const image = images[i % images.length];
            return (
              <Item
                etapa={etapa}
                mdSize={mdSize}
                lgSize={lgSize}
                xlSize={xlSize}
                image={image}
                isProfessor={usuario?.tipoUsuario == EUserType.Professor}
                aula={aula}
                handleAula={isFeriado ? undefined : handleAula}
                handleChamadaDia={isFeriado ? undefined : handleChamadaDia}
                handleDiario={handleDiario}
                aulaTerminou={isFim}
                key={`${i}-${mdSize}-${lgSize}-${xlSize}`}
                percentage={percentage}
                showImpressaoRapida={showImpressaoRapida}
                handleTurmaObs={handleTurmaObs}
              />
            );
          })}
        </Grid>
      )}
      {filter.idTurma && <Float onClick={() => handleShowCadastro()} />}
    </Main>
  );
};
